import { Form, Formik, FormikHelpers, useFormikContext } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiArrowRightCircle, FiPlus } from 'react-icons/fi'
import { MdDeleteForever } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import Button from '../../components/Button'
import Header from '../../components/Header'
import InputSelect from '../../components/InputSelect'
import ModalBox from '../../components/Modal'
import { FileUploader } from '../../components/upload-file/file-uploader'
import { DocumentosList } from '../../utils/listasFixas'

import { uploadFile } from '../../services/upload-file-to-aws'

interface DocumentItem {
  codDocumento: string
  dcrDocumento: string
  urlModelo?: string
  obrigatorio?: boolean
}

interface DocumentFiles {
  [codDocumento: string]: File[]
}

type AttachedFileMeta = {
  id: string
  docCode: string
  docLabel: string
  name: string
  type: string
  lastModified: number
  size: number
  url: string
}

type StoredAttachedFile = {
  docCode: string
  docLabel: string
  name: string
  type: string
  lastModified: number
  size: number
  url: string
}

type FormValues = {
  selectedDocCode: string
}

const LOCALSTORAGE_KEY = '@Iprejun:documentsAttached'

const fileKey = (f: File) => `${f.name}__${f.size}__${f.lastModified}`
const storedKey = (x: StoredAttachedFile) =>
  `${x.docCode}__${x.name}__${x.size}__${x.lastModified}`
const makeId = (docCode: string, f: File) =>
  `${docCode}__${f.name}__${f.lastModified}__${f.size}`

function makeFileWithMetadata(
  name: string,
  type: string,
  lastModified: number,
): File | null {
  if (typeof File === 'undefined') return null
  try {
    return new File([], name, {
      type,
      lastModified: lastModified || Date.now(),
    })
  } catch {
    return null
  }
}

function UploadBySelectedDoc({
  docs,
  documentFiles,
  setDocumentFiles,
}: {
  docs: DocumentItem[]
  documentFiles: DocumentFiles
  setDocumentFiles: React.Dispatch<React.SetStateAction<DocumentFiles>>
}) {
  const { values } = useFormikContext<FormValues>()

  const selectedDoc = useMemo(
    () => docs.find((d) => d.codDocumento === values.selectedDocCode),
    [docs, values.selectedDocCode],
  )

  const selectedDocCode = values.selectedDocCode
  const selectedFiles = documentFiles[selectedDocCode] ?? []

  const setSelectedDocFiles = useCallback(
    (filesOrUpdater: File[] | ((prev: File[]) => File[])) => {
      setDocumentFiles((prev) => {
        const current = prev[selectedDocCode] ?? []

        const rawNext =
          typeof filesOrUpdater === 'function'
            ? filesOrUpdater(current)
            : filesOrUpdater

        const seen = new Set<string>()
        const next = rawNext.filter((file) => {
          const key = fileKey(file)
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

        return { ...prev, [selectedDocCode]: next }
      })
    },
    [selectedDocCode, setDocumentFiles],
  )

  return (
    <div className="flex flex-col gap-3 mt-2 mb-6">
      <p className="text-sm text-[#333]">
        Selecione o documento e faça o upload do arquivo:
      </p>

      <div className="flex flex-col tablet:flex-row tablet:items-end">
        <div className="w-full tablet:w-2/3">
          <InputSelect
            name="selectedDocCode"
            placeholder="Documento"
            options={docs.map((doc) => ({
              label: doc.dcrDocumento,
              value: doc.codDocumento,
            }))}
          />
        </div>

        <div className="w-full tablet:w-1/3">
          <FileUploader
            files={selectedFiles}
            setFiles={setSelectedDocFiles}
            setFieldValue={() => {}}
            fieldName={`docs.${selectedDocCode}`}
          >
            <div className="flex flex-col gap-2">
              <div className="flex gap-3 items-center justify-between">
                <FileUploader.Button
                  disabled={!selectedDocCode}
                  className="w-full"
                  color="blue"
                >
                  <div className="flex flex-row gap-3 font-semibold text-[14px] items-center justify-center">
                    <FiPlus size={20} />
                    Anexar{' '}
                    {selectedDoc
                      ? selectedDoc.dcrDocumento
                          .replace('(Obrigatório)', '')
                          .split(' – ')[0]
                      : ''}
                  </div>
                </FileUploader.Button>
              </div>
            </div>
          </FileUploader>
        </div>
      </div>
    </div>
  )
}

const SupportingDocuments: React.FC = () => {
  const [documentFiles, setDocumentFiles] = useState<DocumentFiles>({})
  const [storedAttachments, setStoredAttachments] = useState<
    StoredAttachedFile[]
  >([])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>('')

  const docs = useMemo(() => DocumentosList as DocumentItem[], [])
  const initialSelectedDocCode = docs?.[0]?.codDocumento ?? ''

  const navigate = useNavigate()

  useEffect(() => {
    const raw = localStorage.getItem(LOCALSTORAGE_KEY)
    if (!raw) return

    try {
      const stored: StoredAttachedFile[] = JSON.parse(raw)
      setStoredAttachments(stored)

      const nextDocumentFiles: DocumentFiles = {}

      stored.forEach((item) => {
        const needsUpload = !item.url || item.url.startsWith('blob:')
        if (!needsUpload) return

        const fakeFile = makeFileWithMetadata(
          item.name,
          item.type,
          item.lastModified,
        )
        if (!fakeFile) return

        if (!nextDocumentFiles[item.docCode])
          nextDocumentFiles[item.docCode] = []
        nextDocumentFiles[item.docCode].push(fakeFile)
      })

      setDocumentFiles(nextDocumentFiles)
    } catch (err) {
      console.error('Erro ao restaurar documentos do localStorage', err)
    }
  }, [])

  const objectUrlMapRef = useRef<Map<string, string>>(new Map())

  const allAttachedFiles = useMemo(() => {
    const items: AttachedFileMeta[] = []
    const nextMap = new Map<string, string>()

    docs.forEach((doc) => {
      const files = documentFiles[doc.codDocumento] ?? []

      files.forEach((file) => {
        const id = makeId(doc.codDocumento, file)

        const prevUrl = objectUrlMapRef.current.get(id)
        const url = prevUrl ?? URL.createObjectURL(file)

        nextMap.set(id, url)

        items.push({
          id,
          docCode: doc.codDocumento,
          docLabel: doc.dcrDocumento,
          name: file.name,
          type: file.type,
          lastModified: file.lastModified,
          size: file.size,
          url,
        })
      })
    })

    objectUrlMapRef.current.forEach((url, id) => {
      if (!nextMap.has(id)) URL.revokeObjectURL(url)
    })
    objectUrlMapRef.current = nextMap

    return items
  }, [docs, documentFiles])

  useEffect(() => {
    return () => {
      objectUrlMapRef.current.forEach((url) => URL.revokeObjectURL(url))
      objectUrlMapRef.current.clear()
    }
  }, [])

  const finalList = useMemo(() => {
    const sessionItems: StoredAttachedFile[] = allAttachedFiles.map((f) => ({
      docCode: f.docCode,
      docLabel: f.docLabel,
      name: f.name,
      type: f.type,
      lastModified: f.lastModified,
      size: f.size,
      url: f.url,
    }))

    const map = new Map<string, StoredAttachedFile>()
    ;[...storedAttachments, ...sessionItems].forEach((item) => {
      map.set(storedKey(item), item)
    })

    return Array.from(map.values())
  }, [allAttachedFiles, storedAttachments])

  const requiredDocs = useMemo(
    () => docs.filter((d) => d.obrigatorio).map((d) => d.codDocumento),
    [docs],
  )

  const missingRequiredDocs = useMemo(() => {
    return requiredDocs.filter((cod) => {
      return !finalList.some((f) => f.docCode === cod)
    })
  }, [finalList, requiredDocs])

  const hasAllRequired = missingRequiredDocs.length === 0

  useEffect(() => {
    if (finalList.length === 0) {
      localStorage.removeItem(LOCALSTORAGE_KEY)
      return
    }
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(finalList))
  }, [finalList])

  const removeFromFinalList = useCallback(
    (item: StoredAttachedFile) => {
      setStoredAttachments((prev) =>
        prev.filter((p) => storedKey(p) !== storedKey(item)),
      )

      setDocumentFiles((prev) => {
        const currentFiles = prev[item.docCode] ?? []
        const updated = currentFiles.filter(
          (file) =>
            !(
              file.name === item.name &&
              file.size === item.size &&
              file.lastModified === item.lastModified
            ),
        )
        return { ...prev, [item.docCode]: updated }
      })

      if (item.url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(item.url)
        } catch {
          // ignore
        }
      }
    },
    [setDocumentFiles, setStoredAttachments],
  )

  const handleSubmit = useCallback(
    async (_values: FormValues, helpers: FormikHelpers<FormValues>) => {
      setSubmitError('')

      try {
        if (!hasAllRequired) {
          const labels = missingRequiredDocs
            .map(
              (cod) => docs.find((d) => d.codDocumento === cod)?.dcrDocumento,
            )
            .filter(Boolean)
            .join(', ')

          setSubmitError(
            `Anexe todos os documentos obrigatórios para continuar.${labels ? ` Faltando: ${labels}` : ''}`,
          )
          return
        }

        setIsSubmitting(true)

        const alreadyUploaded = finalList.filter(
          (f) => f.url && !f.url.startsWith('blob:'),
        )

        const needsUpload = finalList.filter(
          (f) => !f.url || f.url.startsWith('blob:'),
        )

        const uploadedFromAws: StoredAttachedFile[] = []

        for (const item of needsUpload) {
          const files = documentFiles[item.docCode] ?? []
          const realFile = files.find(
            (file) =>
              file.name === item.name &&
              file.size === item.size &&
              file.lastModified === item.lastModified,
          )

          if (!realFile) {
            setStoredAttachments((prev) =>
              prev.filter((stored) => storedKey(stored) !== storedKey(item)),
            )
            throw new Error(
              `O arquivo "${item.name}" precisa ser anexado novamente para envio.`,
            )
          }

          try {
            const url = await uploadFile({ file: realFile })

            uploadedFromAws.push({
              docCode: item.docCode,
              docLabel: item.docLabel,
              name: realFile.name,
              type: realFile.type,
              lastModified: realFile.lastModified,
              size: realFile.size,
              url,
            })
          } catch (err) {
            setDocumentFiles((prev) => {
              const currentFiles = prev[item.docCode] ?? []
              const updatedFiles = currentFiles.filter(
                (file) =>
                  !(
                    file.name === item.name &&
                    file.size === item.size &&
                    file.lastModified === item.lastModified
                  ),
              )

              return {
                ...prev,
                [item.docCode]: updatedFiles,
              }
            })

            setStoredAttachments((prev) =>
              prev.filter((stored) => storedKey(stored) !== storedKey(item)),
            )

            throw err
          }
        }

        const merged = [...alreadyUploaded, ...uploadedFromAws]

        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(merged))

        navigate('/resume')
      } catch (err: unknown) {
        console.error('Erro no submit documentos:', err)
        const msg =
          err instanceof Error ? err.message : 'Falha ao enviar documentos.'
        setSubmitError(msg)
      } finally {
        setIsSubmitting(false)
        helpers.setSubmitting(false)
      }
    },
    [
      documentFiles,
      finalList,
      navigate,
      hasAllRequired,
      missingRequiredDocs,
      docs,
    ],
  )

  return (
    <>
      <Header />

      <div className="mx-auto mb-[15px] flex max-w-[600px] flex-col items-center px-[5px] max-[766px]:max-w-[550px]">
        <Formik<FormValues>
          initialValues={{ selectedDocCode: initialSelectedDocCode }}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ isSubmitting: isFormikSubmitting }) => (
            <Form className="w-full">
              <div className="w-full rounded-[8px] bg-white p-[30px_25px] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] mb-[10px] mx-[5px] max-[766px]:p-[20px_15px]">
                <strong className="mb-[15px] flex justify-center text-center text-[18px] text-[#0071ba]">
                  Documentos
                </strong>

                <UploadBySelectedDoc
                  docs={docs}
                  documentFiles={documentFiles}
                  setDocumentFiles={setDocumentFiles}
                />
                <p className="text-[#C53030] text-xs">
                  Tamanho máximo: 5MB por arquivo.
                </p>

                {finalList.length > 0 && (
                  <div className="mt-7">
                    <strong className="block mb-2 text-[#0071ba]">
                      Arquivos anexados
                    </strong>

                    <div className="flex flex-col gap-2">
                      {finalList.map((f) => (
                        <div
                          key={storedKey(f)}
                          className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-2 rounded-md p-3"
                        >
                          <div className="flex flex-wrap justify-between w-full">
                            <div className="min-w-0">
                              <p
                                className="text-sm font-semibold truncate"
                                title={f.name}
                              >
                                {f.name}
                              </p>
                              <p
                                className="text-xs text-tertiary-3 truncate"
                                title={f.docLabel}
                              >
                                {f.docLabel} • {f.type || 'tipo desconhecido'}
                              </p>
                            </div>

                            <button
                              type="button"
                              className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                              onClick={() => removeFromFinalList(f)}
                              aria-label={`Remover ${f.name}`}
                              title="Remover"
                              disabled={isSubmitting || isFormikSubmitting}
                            >
                              <MdDeleteForever size={18} />
                              Remover
                            </button>
                          </div>
                          <hr />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 w-full flex justify-center  flex-col items-center">
                <Button
                  type="submit"
                  color="blue"
                  className="min-w-fit gap-3 px-10"
                  disabled={
                    isSubmitting || isFormikSubmitting || !hasAllRequired
                  }
                >
                  {isSubmitting ? 'Enviando...' : 'Continuar'}
                  <FiArrowRightCircle size={24} />
                </Button>
                {!hasAllRequired && (
                  <p className="mb-3 text-xs text-[#C53030] text-center">
                    Anexe todos os documentos obrigatórios para continuar.
                  </p>
                )}
              </div>
            </Form>
          )}
        </Formik>

        <button
          type="button"
          onClick={() => navigate('/service-time')}
          className="mb-[15px] flex flex-col items-center justify-center border-0 border-b border-transparent bg-transparent text-[#333] hover:border-[#333] focus:border-[#333]"
          disabled={isSubmitting}
        >
          &lt; Anterior
        </button>

        <ModalBox
          isOpen={!!submitError}
          onRequestClose={() => setSubmitError('')}
        >
          <p>{submitError}</p>
        </ModalBox>
      </div>
    </>
  )
}

export default SupportingDocuments
0
